/**
 * NotificationOverlay - Popup visuel pour les alertes HAL 3000
 * Affiche les notifications Home Connect, charge VE, etc.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { X, Bell, CheckCircle, AlertTriangle, Thermometer, VolumeX, Zap, Car, StopCircle } from 'lucide-react';

const ICON_MAP = {
  'check-circle': CheckCircle,
  'alert-triangle': AlertTriangle,
  'thermometer': Thermometer,
  'volume-x': VolumeX,
  'bell': Bell,
};

const COLOR_MAP = {
  green: { bg: 'rgba(34, 197, 94, 0.15)', border: '#22c55e', text: '#4ade80' },
  orange: { bg: 'rgba(249, 115, 22, 0.15)', border: '#f97316', text: '#fb923c' },
  red: { bg: 'rgba(239, 68, 68, 0.15)', border: '#ef4444', text: '#f87171' },
  yellow: { bg: 'rgba(234, 179, 8, 0.15)', border: '#eab308', text: '#facc15' },
  blue: { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6', text: '#60a5fa' },
  cyan: { bg: 'rgba(6, 182, 212, 0.15)', border: '#06b6d4', text: '#22d3ee' },
};

const NotificationToast = ({ notification, onDismiss }) => {
  const visual = notification.visual || {};
  const IconComponent = ICON_MAP[visual.icon] || Bell;
  const colors = COLOR_MAP[visual.color] || COLOR_MAP.blue;

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(), 15000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      data-testid="notification-toast"
      className="flex items-center gap-3 p-4 rounded-xl border-2 backdrop-blur-md animate-slide-in"
      style={{
        background: colors.bg,
        borderColor: colors.border,
        boxShadow: `0 0 30px ${colors.border}40`,
        minWidth: '350px',
      }}
    >
      <IconComponent className="w-8 h-8 flex-shrink-0" style={{ color: colors.text }} />
      <div className="flex-1">
        <div className="text-sm font-mono font-bold" style={{ color: colors.text }}>
          {visual.title || notification.event_key}
        </div>
        <div className="text-xs font-mono text-gray-400 mt-0.5">
          {visual.message || notification.appliance_name}
        </div>
      </div>
      <button
        onClick={onDismiss}
        className="p-1 rounded-full hover:bg-white/10 transition-colors"
        data-testid="dismiss-notification"
      >
        <X className="w-5 h-5 text-gray-500" />
      </button>
    </div>
  );
};

const EVChargingPopup = ({ chargerData, onDismiss, onEmergencyStop }) => {
  if (!chargerData || !chargerData.is_charging) return null;

  return (
    <div
      data-testid="ev-charging-popup"
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="relative p-8 rounded-2xl border-2 animate-pulse-slow"
        style={{
          background: 'linear-gradient(135deg, #0a1628 0%, #0d2847 100%)',
          borderColor: '#22c55e',
          boxShadow: '0 0 60px rgba(34, 197, 94, 0.3)',
          minWidth: '450px',
        }}
      >
        {/* Close */}
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-gray-500"
          data-testid="ev-popup-close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/40">
            <Car className="w-10 h-10 text-green-400" />
          </div>
          <div>
            <div className="text-2xl font-mono font-bold text-green-400">
              CHARGE EN COURS
            </div>
            <div className="text-sm font-mono text-gray-500">
              Chargeur VE 7,4 kW
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-black/40 rounded-xl border border-green-600/30">
            <div className="text-xs font-mono text-gray-500">PUISSANCE</div>
            <div className="text-3xl font-mono font-bold text-green-400">
              <Zap className="w-5 h-5 inline mr-1" />
              {chargerData.power_kw || '7,4'} kW
            </div>
          </div>
          <div className="p-4 bg-black/40 rounded-xl border border-cyan-600/30">
            <div className="text-xs font-mono text-gray-500">ÉNERGIE CHARGÉE</div>
            <div className="text-3xl font-mono font-bold text-cyan-400">
              {chargerData.last_charge_kwh || 0} kWh
            </div>
          </div>
          <div className="p-4 bg-black/40 rounded-xl border border-yellow-600/30">
            <div className="text-xs font-mono text-gray-500">COÛT ESTIMÉ</div>
            <div className="text-3xl font-mono font-bold text-yellow-400">
              {(chargerData.last_charge_cost || 0).toFixed(2)}€
            </div>
          </div>
          <div className="p-4 bg-black/40 rounded-xl border border-purple-600/30">
            <div className="text-xs font-mono text-gray-500">STATUT</div>
            <div className="text-xl font-mono font-bold text-purple-400">
              {chargerData.status === 'charging' ? 'EN CHARGE' : chargerData.status?.toUpperCase() || 'ACTIF'}
            </div>
          </div>
        </div>

        {/* Emergency Stop */}
        <button
          onClick={onEmergencyStop}
          className="w-full py-4 bg-red-600 hover:bg-red-700 rounded-xl border-2 border-red-500 transition-colors flex items-center justify-center gap-3"
          data-testid="ev-emergency-stop"
        >
          <StopCircle className="w-7 h-7 text-white" />
          <span className="text-xl font-mono font-bold text-white">ARRÊT D'URGENCE</span>
        </button>
      </div>
    </div>
  );
};

const NotificationOverlay = () => {
  const [notifications, setNotifications] = useState([]);
  const [evCharger, setEvCharger] = useState(null);
  const [evPopupDismissed, setEvPopupDismissed] = useState(false);
  const [evPopupShown, setEvPopupShown] = useState(false);

  // Poll notifications
  const pollNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications?unread_only=true&limit=5');
      if (res.data?.success && res.data.notifications?.length > 0) {
        setNotifications(res.data.notifications);
      }
    } catch {}
  }, []);

  // Poll EV charger
  const pollCharger = useCallback(async () => {
    try {
      const res = await api.get('/integrations/vehicles/charger/status');
      if (res.data?.success && res.data.charger) {
        const charger = res.data.charger;
        setEvCharger(charger);
        
        // Show popup when charging detected (only once per session)
        if (charger.is_charging && !evPopupDismissed && !evPopupShown) {
          setEvPopupShown(true);
        }
        // Reset when charging stops
        if (!charger.is_charging) {
          setEvPopupDismissed(false);
          setEvPopupShown(false);
        }
      }
    } catch {}
  }, [evPopupDismissed, evPopupShown]);

  useEffect(() => {
    pollNotifications();
    pollCharger();
    const interval = setInterval(() => {
      pollNotifications();
      pollCharger();
    }, 30000);
    return () => clearInterval(interval);
  }, [pollNotifications, pollCharger]);

  const dismissNotification = async (index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
    // Mark as read if no more visible
    if (notifications.length <= 1) {
      await api.post('/notifications/read-all').catch(() => {});
    }
  };

  const dismissEvPopup = () => {
    setEvPopupDismissed(true);
  };

  const emergencyStopCharger = async () => {
    try {
      await api.post('/integrations/tuya/devices/bf8fdeb1d095efcc60kdqz/turn-off');
      setEvPopupDismissed(true);
    } catch {}
  };

  return (
    <>
      {/* Notification Toasts - Top Right */}
      <div className="fixed top-4 right-4 z-[9998] flex flex-col gap-2" data-testid="notification-container">
        {notifications.map((n, i) => (
          <NotificationToast
            key={`${n.event_key}-${n.created_at}`}
            notification={n}
            onDismiss={() => dismissNotification(i)}
          />
        ))}
      </div>

      {/* EV Charging Popup */}
      {evPopupShown && !evPopupDismissed && evCharger?.is_charging && (
        <EVChargingPopup
          chargerData={evCharger}
          onDismiss={dismissEvPopup}
          onEmergencyStop={emergencyStopCharger}
        />
      )}
    </>
  );
};

export default NotificationOverlay;
